import React, {FC} from 'react';
import styled from 'styled-components';

import {AsyncStatusesEnum} from '../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {Skeleton, SkeletonColorSchemesEnum} from '../../../../libs/shared/ui/src/components/utility/skeleton';
import {CrmSyncModel, CrmSyncSourcesEnum} from '../../../models/api/crmSyncModel';
import {AccountDetailsSectionsEnum} from '../../../models/api/localPreferencesModel';
import {CrmPreviewPanelState} from '../../../store/panels/contacts/contactsManager/crmPreview/crmPreviewState';
import {AccountDetailsHeaderSkeleton} from '../accountDetails/loadingSkeletons/accountDetailsHeaderSkeleton';
import {AccountDetailsSectionSkeleton} from '../accountDetails/loadingSkeletons/accountDetailsSectionSkeleton';
import {AccountCrmPreview} from './accountCrmPreview';
import {CardCrmPreview} from './cardCrmPreview';
import {CrmPreviewError} from './crmPreviewError';

/*
 * Props.
 */

interface CrmPreviewProps {
  panelState: CrmPreviewPanelState;
  crmSync?: CrmSyncModel;
  updateSelectedId: (arg: number) => void;
}

/*
 * Style.
 */

const StyledCrmPreviewWrapperDiv = styled.div`
  background: ${(p) => p.theme.greys.shade30};
  padding: 16px;
  height: 100%;
  overflow: auto;
`;

const StyledDropdownTitleSkeleton = styled(Skeleton)`
  margin-bottom: 6px;
`;

const StyledPreviewWrapperDiv = styled.div`
  margin-top: 16px;
  background: ${(p) => p.theme.greys.white};
  border-radius: 8px;
`;

/*
 * Component.
 */

export const CrmPreview: FC<CrmPreviewProps> = (props) => {
  const {
    crmSync,
    panelState: {status, accounts, cards, selectedId, crmSyncSource},
    updateSelectedId,
  } = props;

  const isLoading = !status || status === AsyncStatusesEnum.LOADING;

  if (isLoading) {
    return renderLoadingState();
  }

  const isErrored = status === AsyncStatusesEnum.FAILED;
  const selectedCard = cards?.find((card) => card.id === selectedId);
  const selectedAccount = accounts?.find((account) => account.id === selectedId);

  if (!crmSync || isErrored || (!selectedCard && !selectedAccount)) {
    return renderErrorState(crmSyncSource);
  }

  if (accounts && accounts.length > 0 && selectedAccount) {
    return (
      <AccountCrmPreview
        source={crmSyncSource}
        selectedAccount={selectedAccount}
        accounts={accounts}
        updateSelectedId={updateSelectedId}
      />
    );
  }

  if (cards && cards.length > 0 && selectedCard) {
    return (
      <CardCrmPreview
        source={crmSyncSource}
        selectedCard={selectedCard}
        cards={cards}
        updateSelectedId={updateSelectedId}
      />
    );
  }

  return renderErrorState(crmSyncSource);
};

/*
 * Helpers.
 */

function renderErrorState(crmSyncSource: CrmSyncSourcesEnum) {
  return (
    <StyledCrmPreviewWrapperDiv>
      <CrmPreviewError crmSyncSource={crmSyncSource} />
    </StyledCrmPreviewWrapperDiv>
  );
}

function renderLoadingState() {
  return (
    <StyledCrmPreviewWrapperDiv>
      <StyledDropdownTitleSkeleton scheme={SkeletonColorSchemesEnum.GREY40} width="178px" height="16px" />
      <Skeleton scheme={SkeletonColorSchemesEnum.GREY40} width="100%" height="30px" />
      <StyledPreviewWrapperDiv>
        <AccountDetailsHeaderSkeleton />
        <AccountDetailsSectionSkeleton section={AccountDetailsSectionsEnum.ACCOUNT_INFORMATION} />
      </StyledPreviewWrapperDiv>
    </StyledCrmPreviewWrapperDiv>
  );
}
